import { useMemo } from 'react'
import { TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TrendChart } from './TrendChart'
import type { TimeseriesPoint } from '@/hooks/useAdminStats'

type TimePeriod = 1 | 7 | 30

interface MetricCardProps {
  title: string
  timeseries: TimeseriesPoint[]
  total?: number
  format?: 'number' | 'time' | 'percent' | 'decimal'
  suffix?: string
  loading?: boolean
  period: TimePeriod
  className?: string
}

/**
 * Format a number based on type
 */
function formatValue(value: number, format: 'number' | 'time' | 'percent' | 'decimal'): string {
  switch (format) {
    case 'time':
      if (value >= 60) {
        const hours = Math.floor(value / 60)
        const mins = value % 60
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
      }
      return `${value}m`
    case 'percent':
      return `${value}%`
    case 'decimal':
      return value.toFixed(1)
    default:
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`
      }
      return value.toLocaleString()
  }
}

/**
 * Calculate sum for a time period
 */
function sumForPeriod(timeseries: TimeseriesPoint[], days: number): number {
  const slice = timeseries.slice(-days)
  return slice.reduce((sum, point) => sum + point.count, 0)
}

/**
 * Calculate percentage change vs previous period
 */
function calculateChange(timeseries: TimeseriesPoint[], days: number): number {
  if (timeseries.length < days * 2) return 0
  
  const currentPeriod = timeseries.slice(-days)
  const previousPeriod = timeseries.slice(-days * 2, -days)
  
  const currentSum = currentPeriod.reduce((sum, p) => sum + p.count, 0)
  const previousSum = previousPeriod.reduce((sum, p) => sum + p.count, 0)
  
  if (previousSum === 0) return currentSum > 0 ? 100 : 0
  return Math.round(((currentSum - previousSum) / previousSum) * 100)
}

export function MetricCard({
  title,
  timeseries,
  total,
  format = 'number',
  suffix,
  loading = false,
  period,
  className,
}: MetricCardProps) {
  const { value, change, chartData } = useMemo(() => {
    if (!timeseries || timeseries.length === 0) {
      return { value: 0, change: 0, chartData: [] }
    }

    const periodData = timeseries.slice(-period)
    const periodSum = sumForPeriod(timeseries, period)
    const periodChange = calculateChange(timeseries, period)

    return {
      value: total !== undefined ? total : periodSum,
      change: periodChange,
      chartData: periodData.map(p => ({ date: p.date, value: p.count })),
    }
  }, [timeseries, period, total])

  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02] p-4',
        'transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.03]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
          {title}
        </span>
        {!loading && change !== 0 && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full',
              isPositive && 'text-primary bg-primary/10',
              isNegative && 'text-red-400 bg-red-400/10',
              !isPositive && !isNegative && 'text-muted-foreground/50 bg-white/[0.03]'
            )}
          >
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : isNegative ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {/* Value */}
      {loading ? (
        <div className="flex items-center gap-2 mb-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
        </div>
      ) : (
        <div className="mb-3">
          <span className="text-2xl font-semibold text-foreground tabular-nums">
            {formatValue(value, format)}
          </span>
          {suffix && (
            <span className="ml-1 text-sm text-muted-foreground/70">
              {suffix}
            </span>
          )}
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-[80px] bg-white/[0.02] rounded animate-pulse" />
      ) : (
        <TrendChart
          data={chartData}
          height={80}
          showTrendline={chartData.length >= 3}
        />
      )}

      {/* Period indicator */}
      <div className="mt-2 text-[10px] text-muted-foreground/40">
        Last {period} day{period > 1 ? 's' : ''}
      </div>
    </div>
  )
}

/**
 * Metric card for session time (different data structure)
 */
interface SessionMetricCardProps {
  title: string
  avgMinutes: number
  timeseries: { date: string; avgMinutes: number; totalMinutes: number }[]
  loading?: boolean
  period: TimePeriod
  showTotal?: boolean
  className?: string
}

export function SessionMetricCard({
  title,
  avgMinutes,
  timeseries,
  loading = false,
  period,
  showTotal = false,
  className,
}: SessionMetricCardProps) {
  const { displayValue, change, chartData } = useMemo(() => {
    if (!timeseries || timeseries.length === 0) {
      return { displayValue: 0, change: 0, chartData: [] }
    }

    const periodData = timeseries.slice(-period)
    
    // Calculate change
    const currentPeriod = timeseries.slice(-period)
    const previousPeriod = timeseries.slice(-period * 2, -period)
    
    const currentAvg = currentPeriod.length > 0
      ? currentPeriod.reduce((sum, p) => sum + (showTotal ? p.totalMinutes : p.avgMinutes), 0) / (showTotal ? 1 : currentPeriod.length)
      : 0
    const previousAvg = previousPeriod.length > 0
      ? previousPeriod.reduce((sum, p) => sum + (showTotal ? p.totalMinutes : p.avgMinutes), 0) / (showTotal ? 1 : previousPeriod.length)
      : 0
    
    const periodChange = previousAvg === 0 
      ? (currentAvg > 0 ? 100 : 0)
      : Math.round(((currentAvg - previousAvg) / previousAvg) * 100)

    return {
      displayValue: showTotal 
        ? periodData.reduce((sum, p) => sum + p.totalMinutes, 0)
        : avgMinutes,
      change: periodChange,
      chartData: periodData.map(p => ({ 
        date: p.date, 
        value: showTotal ? p.totalMinutes : p.avgMinutes 
      })),
    }
  }, [timeseries, period, avgMinutes, showTotal])

  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02] p-4',
        'transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.03]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
          {title}
        </span>
        {!loading && change !== 0 && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full',
              isPositive && 'text-primary bg-primary/10',
              isNegative && 'text-red-400 bg-red-400/10',
              !isPositive && !isNegative && 'text-muted-foreground/50 bg-white/[0.03]'
            )}
          >
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : isNegative ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {/* Value */}
      {loading ? (
        <div className="flex items-center gap-2 mb-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
        </div>
      ) : (
        <div className="mb-3">
          <span className="text-2xl font-semibold text-foreground tabular-nums">
            {formatValue(displayValue, 'time')}
          </span>
          <span className="ml-2 text-xs text-muted-foreground/50">
            {showTotal ? 'total' : 'avg'}
          </span>
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-[80px] bg-white/[0.02] rounded animate-pulse" />
      ) : (
        <TrendChart
          data={chartData}
          height={80}
          showTrendline={chartData.length >= 3}
        />
      )}

      {/* Period indicator */}
      <div className="mt-2 text-[10px] text-muted-foreground/40">
        Last {period} day{period > 1 ? 's' : ''}
      </div>
    </div>
  )
}

/**
 * Metric card for cumulative data (like total users)
 */
interface CumulativeMetricCardProps {
  title: string
  total: number
  timeseries: { date: string; cumulative: number }[]
  loading?: boolean
  period: TimePeriod
  className?: string
}

export function CumulativeMetricCard({
  title,
  total,
  timeseries,
  loading = false,
  period,
  className,
}: CumulativeMetricCardProps) {
  const { chartData, change } = useMemo(() => {
    if (!timeseries || timeseries.length === 0) {
      return { chartData: [], change: 0 }
    }

    const periodData = timeseries.slice(-period)
    
    // For cumulative, calculate growth rate
    const startValue = periodData[0]?.cumulative || 0
    const endValue = periodData[periodData.length - 1]?.cumulative || 0
    const periodChange = startValue === 0 
      ? (endValue > 0 ? 100 : 0)
      : Math.round(((endValue - startValue) / startValue) * 100)

    return {
      chartData: periodData.map(p => ({ date: p.date, value: p.cumulative })),
      change: periodChange,
    }
  }, [timeseries, period])

  const isPositive = change > 0
  const isNegative = change < 0

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02] p-4',
        'transition-all duration-200 hover:border-white/[0.1] hover:bg-white/[0.03]',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wider">
          {title}
        </span>
        {!loading && change !== 0 && (
          <div
            className={cn(
              'flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full',
              isPositive && 'text-primary bg-primary/10',
              isNegative && 'text-red-400 bg-red-400/10',
              !isPositive && !isNegative && 'text-muted-foreground/50 bg-white/[0.03]'
            )}
          >
            {isPositive ? (
              <TrendingUp className="h-3 w-3" />
            ) : isNegative ? (
              <TrendingDown className="h-3 w-3" />
            ) : (
              <Minus className="h-3 w-3" />
            )}
            <span>{Math.abs(change)}%</span>
          </div>
        )}
      </div>

      {/* Value */}
      {loading ? (
        <div className="flex items-center gap-2 mb-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
        </div>
      ) : (
        <div className="mb-3">
          <span className="text-2xl font-semibold text-foreground tabular-nums">
            {total.toLocaleString()}
          </span>
          <span className="ml-2 text-xs text-muted-foreground/50">
            total
          </span>
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-[80px] bg-white/[0.02] rounded animate-pulse" />
      ) : (
        <TrendChart
          data={chartData}
          height={80}
          showTrendline={chartData.length >= 3}
        />
      )}

      {/* Period indicator */}
      <div className="mt-2 text-[10px] text-muted-foreground/40">
        Last {period} day{period > 1 ? 's' : ''}
      </div>
    </div>
  )
}
